import { gql } from "config/graphql";
import {
  IWyshAllocation,
  IWyshAllocationRecipient,
  MutationResponse,
  SuccessResponse,
  WyshAllocationRecipientType,
} from "config/types";
import { RECIPIENT_FIELDS, WYSH_FIELDS } from "gql/fragments";
import { getApolloClient } from "store/apollo";
import { assertDefined } from "utils/error";

export interface ICreateWyshAllocationInput {
  wyshId: string;
  name?: string;
}

interface ICreateWyshAllocationData {
  createWyshAllocation: {
    wyshAllocation: IWyshAllocation;
  };
}

export const createWyshAllocationMutation: MutationResponse<
  ICreateWyshAllocationInput,
  ICreateWyshAllocationData
> = async (input: ICreateWyshAllocationInput) => {
  const client = getApolloClient();

  const mutation = gql`
    ${RECIPIENT_FIELDS}
    ${WYSH_FIELDS}
    mutation CreateWyshAllocation($input: CreateWyshAllocationInput!) {
      createWyshAllocation(input: $input) {
        wyshAllocation {
          id
          name
          amount
          covered
          amountCovered
          percentCovered
          monitored
          status
          order
          terminatesAt
          recipient {
            ...RecipientFields
          }
          wysh {
            ...WyshFields
          }
        }
      }
    }
  `;

  const { data } = await client.mutate<ICreateWyshAllocationData>({
    variables: { input },
    mutation,
  });
  return { data: assertDefined(data) };
};

export interface IUpdateWyshAllocationInput {
  wyshAllocationId: string;
  name?: string;
  amount?: number;
  monitored?: boolean;
  order?: number;
  terminatesAt?: string;
}

interface IUpdateWyshAllocationData {
  updateWyshAllocation: {
    wyshAllocation: IWyshAllocation;
  };
}

export const updateWyshAllocationMutation: MutationResponse<
  IUpdateWyshAllocationInput,
  IUpdateWyshAllocationData
> = async (input: IUpdateWyshAllocationInput) => {
  const client = getApolloClient();

  const mutation = gql`
    ${RECIPIENT_FIELDS}
    ${WYSH_FIELDS}
    mutation UpdateWyshAllocation($input: UpdateWyshAllocationInput!) {
      updateWyshAllocation(input: $input) {
        wyshAllocation {
          id
          name
          amount
          covered
          amountCovered
          percentCovered
          monitored
          status
          order
          terminatesAt
          recipient {
            ...RecipientFields
          }
          wysh {
            ...WyshFields
          }
        }
      }
    }
  `;

  const { data } = await client.mutate<IUpdateWyshAllocationData>({
    variables: { input },
    mutation,
  });
  return { data: assertDefined(data) };
};

interface IDestroyWyshAllocationData {
  decommissionWyshAllocation: SuccessResponse;
}

export const destroyWyshAllocationMutation: MutationResponse<
  string,
  IDestroyWyshAllocationData
> = async (wyshAllocationId: string) => {
  const client = getApolloClient();

  const mutation = gql`
    mutation DecommissionWyshAllocation($input: DecommissionWyshAllocationInput!) {
      decommissionWyshAllocation(input: $input) {
        response {
          success
        }
      }
    }
  `;

  const { data } = await client.mutate<IDestroyWyshAllocationData>({
    variables: {
      input: { wyshAllocationId },
    },
    mutation,
  });
  return { data: assertDefined(data) };
};

export interface ICreateAllocationRecipientInput {
  wyshAllocationId: string;
  recipient: Omit<IWyshAllocationRecipient, "id" | "__typename">;
  type: WyshAllocationRecipientType;
}

interface ICreateWyshAllocationRecipientData {
  createAllocationRecipient: {
    wyshAllocation: {
      recipient: IWyshAllocationRecipient;
    };
  };
}

// Recipient
export const createAllocationRecipientMutation: MutationResponse<
  ICreateAllocationRecipientInput,
  ICreateWyshAllocationRecipientData
> = async ({ wyshAllocationId, recipient, type }: ICreateAllocationRecipientInput) => {
  const client = getApolloClient();

  const mutation = gql`
    ${RECIPIENT_FIELDS}
    mutation CreateAllocationRecipient($input: CreateAllocationRecipientInput!) {
      createAllocationRecipient(input: $input) {
        wyshAllocation {
          recipient {
            ...RecipientFields
          }
        }
      }
    }
  `;

  const { data } = await client.mutate<ICreateWyshAllocationRecipientData>({
    variables: {
      input: {
        wyshAllocationId,
        recipient: {
          [type]: recipient,
        },
      },
    },
    mutation,
  });
  return { data: assertDefined(data) };
};

export interface IUpdateAllocationRecipientInput {
  wyshAllocationId: string;
  recipient: Omit<IWyshAllocationRecipient, "id" | "__typename">;
  type: WyshAllocationRecipientType;
}

interface IUpdateWyshAllocationRecipientData {
  updateAllocationRecipient: {
    wyshAllocation: {
      recipient: IWyshAllocationRecipient;
    };
  };
}

export const updateAllocationRecipientMutation: MutationResponse<
  IUpdateAllocationRecipientInput,
  IUpdateWyshAllocationRecipientData
> = async ({ wyshAllocationId, recipient, type }: IUpdateAllocationRecipientInput) => {
  const client = getApolloClient();

  const mutation = gql`
    ${RECIPIENT_FIELDS}
    mutation UpdateAllocationRecipient($input: UpdateAllocationRecipientInput!) {
      updateAllocationRecipient(input: $input) {
        wyshAllocation {
          recipient {
            ...RecipientFields
          }
        }
      }
    }
  `;

  const { data } = await client.mutate<IUpdateWyshAllocationRecipientData>({
    variables: {
      input: {
        wyshAllocationId,
        recipient: {
          [type]: recipient,
        },
      },
    },
    mutation,
  });
  return { data: assertDefined(data) };
};

interface IRemoveAllocationRecipientInput {
  wyshAllocationId: string;
}
interface IRemoveAllocationRecipientData {
  destroyAllocationRecipient: SuccessResponse;
}

export const removeAllocationRecipientMutation: MutationResponse<
  IRemoveAllocationRecipientInput,
  IRemoveAllocationRecipientData
> = async ({ wyshAllocationId }: IRemoveAllocationRecipientInput) => {
  const client = getApolloClient();

  const mutation = gql`
    mutation DestroyAllocationRecipient($input: DestroyAllocationRecipientInput!) {
      destroyAllocationRecipient(input: $input) {
        success
      }
    }
  `;

  const { data } = await client.mutate<IRemoveAllocationRecipientData>({
    variables: { input: { wyshAllocationId } },
    mutation,
  });
  return { data: assertDefined(data) };
};
